import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { Badge } from "@/components/Badge/Badge";
import { FavoriteButton } from "../../FavoriteButton/FavoriteButton";
import { useResourceCardV2 } from "@/modules/hub/features/ResourceLibrary/hooks/useResourceCardV2";
export { getResourceTypeIcon } from "@/modules/hub/features/ResourceLibrary/hooks/useResourceCardV2";

interface ResourceCardV2Props {
  resource: GetResourceSearchResultsItem;
  showFeaturedBadge?: boolean;
  onToggleFavorite?: (uuid: string) => void | Promise<void>;
}

export function ResourceCardV2({
  resource,
  showFeaturedBadge = false,
  onToggleFavorite,
}: ResourceCardV2Props) {
  const {
    isFavorited,
    taxonomyLabel,
    description,
    tradeBadge,
    tradeBadgeClass,
    tradeColorClass,
    TypeIcon,
    typeGradient,
    typeLabel,
    isPartnerPage,
    href,
    thumbnailUrl,
    handleCardClick,
    handleToggleFavorite,
  } = useResourceCardV2(resource, onToggleFavorite);

  const thumbnail = (
    <div className="relative aspect-video overflow-hidden shrink-0">
      {thumbnailUrl ? (
        <img
          alt={resource.title}
          className="block w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          src={thumbnailUrl}
        />
      ) : (
        <div
          className={`w-full h-full flex items-center justify-center relative overflow-hidden bg-gradient-to-b ${typeGradient}`}
        >
          <svg
            className="absolute inset-0 w-full h-full opacity-20"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
              fill="white"
            />
            <path
              d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
              fill="white"
              opacity="0.3"
            />
          </svg>
          <TypeIcon className="h-12 w-12 text-white relative z-10 opacity-90" />
        </div>
      )}
    </div>
  );

  const content = (
    <div className="p-4 flex flex-col flex-1">
      {(taxonomyLabel || tradeBadge) && (
        <div className="flex items-start justify-between gap-2">
          <div
            className={`text-[11px] font-semibold uppercase tracking-wide truncate ${
              tradeColorClass ?? "text-gray-900 dark:text-white"
            }`}
            title={taxonomyLabel}
          >
            {taxonomyLabel}
          </div>
          {tradeBadge && (
            <Badge
              className={`shrink-0 border-transparent px-2 py-1 text-[9px] leading-none font-bold uppercase tracking-wide ${tradeBadgeClass}`}
              variant="outline"
            >
              {tradeBadge.label}
            </Badge>
          )}
        </div>
      )}

      <h3
        className={`mt-2 text-[15px] font-bold leading-snug line-clamp-2 transition-colors ${
          tradeColorClass ?? "text-gray-900 dark:text-white"
        }`}
      >
        {resource.title}
      </h3>

      {description && (
        <p className="mt-2 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400 line-clamp-3">
          {description}
        </p>
      )}

      <div className="mt-auto pt-4 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
        {/* Resource type as icon + text, icon chosen by resource type */}
        <span className="flex items-center gap-1.5 min-w-0">
          <TypeIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{typeLabel}</span>
        </span>

        <span
          className={`font-bold shrink-0 pl-2 ${
            tradeColorClass ?? "text-gray-900 dark:text-white"
          }`}
        >
          View
        </span>
      </div>
    </div>
  );

  const cardInner = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden h-full flex flex-col">
      {thumbnail}
      {content}
    </div>
  );

  return (
    <div className="block group relative h-full">
      {/* Optional Featured Badge */}
      {showFeaturedBadge && resource.isFeatured && (
        <div className="absolute -top-2 -right-2" style={{ zIndex: 1 }}>
          <div className="bg-yellow-400 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-100 text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
            <Star className="h-3 w-3 fill-current" />
            FEATURED
          </div>
        </div>
      )}

      {/* Favorite Button */}
      <div className="absolute top-2 left-2" style={{ zIndex: 1 }}>
        <FavoriteButton
          isFavorited={isFavorited}
          onToggle={handleToggleFavorite}
        />
      </div>

      {isPartnerPage ? (
        <a
          className="block h-full"
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {cardInner}
        </a>
      ) : (
        <Link className="block h-full" onClick={handleCardClick} to={href}>
          {cardInner}
        </Link>
      )}
    </div>
  );
}
